import { FC, useEffect, useState } from 'react';
import { ActionIcon, Flex, Group, TextInput } from '@mantine/core';
import { IconDragDrop, IconDragDrop2, IconPhoto, IconX } from '@tabler/icons';
import { useDrag, useDrop } from 'react-dnd-latest';
import { AnswerDto } from '../../Types/QuestionnaireT';
import { EventRichInput, RichInput } from '../tiptap/TiptapEditorInput';
import DocumentBubbleMenu from '../tiptap/toolbars/BubbleMenu';
import { QuestionBundleS } from '../../Service/QuestionBundleS';
import { TiptapS } from '../../Service/TiptapS';
import { useEventScope } from '../../Service/hooks/useEventScope';
import { BookitupEntity, FileS } from '../../Service/FileS';
import LinkText from '../../Atoms/LinkText';
import { openFilePickerModal } from '../../Molecules/Dialogs/FilePickerModal';
import { useJwtClaims } from '../../Service/Context/AuthContext';
import PdfPreview from '../../Molecules/Document/PdfPreviewNew';

interface Props {
  questionId: string;
  answer: AnswerDto;
  handleChange: (patch: Partial<AnswerDto>) => void;
  moveAnswer?: (answerId: string, targetAnswerId: string) => void;
  onDelete: () => void;
  isAdditional?: boolean;
}

const AnswerOptionItem: FC<Props> = ({
  questionId,
  answer,
  handleChange,
  onDelete,
  moveAnswer,
  isAdditional = false,
}) => {
  const { id: answerId, value, image } = answer;
  const { isEventScope } = useEventScope();

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: 'answer',
      canDrop: (item: AnswerDto) => item.id !== answerId,
      collect: (monitor: any) => ({
        isOver: monitor.isOver() && monitor.getItem().id !== answerId,
        canDrop: !!monitor.canDrop(),
      }),
      drop: async (item: AnswerDto) => {
        if (moveAnswer) {
          moveAnswer(item.id, answerId);
        }
      },
    }),
    [answerId],
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'answer',
      item: answer,
      collect: (monitor: any) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [answerId],
  );

  const onPictureSelect = async (entity: BookitupEntity | null) => {
    const resJSON = await QuestionBundleS.editAnswer(answerId, { image: entity });
    if (resJSON) {
      handleChange({ image: entity });
    }
  };

  const deleteHandler = async () => {
    const deleted = await QuestionBundleS.deleteAnswer(answerId, questionId);
    if (deleted) {
      onDelete();
    }
  };

  return (
    <>
      <Flex ref={drop} gap="xs" align="center" sx={{ opacity: isDragging ? 0.5 : 1 }}>
        {!isAdditional && (
          <ActionIcon ref={drag}>
            {canDrop && <IconDragDrop color="#1D5191" size={isOver ? 28 : 20} />}
            {!canDrop && <IconDragDrop2 size={20} style={{ cursor: 'grab' }} />}
          </ActionIcon>
        )}
        <div style={{ flexGrow: 1 }}>
          {isAdditional && <TextInput disabled value={value} />}
          {!isAdditional && (
            <>
              {isEventScope && (
                <EventRichInput
                  value={value ?? ''}
                  patchField={async (htmlString) => {
                    const resJSON = await QuestionBundleS.editAnswer(answerId, { value: htmlString });
                    if (resJSON) {
                      handleChange({ value: htmlString });
                      return true;
                    }
                    return false;
                  }}
                  menu={<DocumentBubbleMenu />}
                  styles={TiptapS.TextInputStyles}
                />
              )}
              {!isEventScope && (
                <RichInput
                  value={value ?? ''}
                  patchField={async (htmlString) => {
                    const resJSON = await QuestionBundleS.editAnswer(answerId, { value: htmlString });
                    if (resJSON) {
                      handleChange({ value: htmlString });
                      return true;
                    }
                    return false;
                  }}
                  menu={<DocumentBubbleMenu />}
                  styles={TiptapS.TextInputStyles}
                />
              )}
            </>
          )}
        </div>
        <ActionIcon size="xs" onClick={deleteHandler}>
          <IconX />
        </ActionIcon>
      </Flex>
      {!isAdditional && <AnswerImage entity={image} onPictureSelect={onPictureSelect} />}
    </>
  );
};

const AnswerImage: FC<{ entity: BookitupEntity | null; onPictureSelect: (entity: BookitupEntity | null) => void }> = ({
  entity,
  onPictureSelect,
}) => {
  const { profileId } = useJwtClaims();
  const [fileExists, setFileExists] = useState(true);
  const { isEventScope, eventId } = useEventScope();
  const { entityId, entityType, fileName } = entity ?? {};

  useEffect(() => {
    if (entityType === 'profiles' && entityId && fileName) {
      FileS.isFileExists(entityType, entityId, fileName).then(setFileExists);
    }
  }, [entityType, entityId, fileName]);

  if (entity === null) {
    return (
      <LinkText
        ml={40}
        onClick={() =>
          openFilePickerModal({
            eventId: isEventScope ? eventId : undefined,
            profileId: profileId.toString(),
            onSelect: onPictureSelect,
            allowedExtensions: ['jpg', 'jpeg', 'png', 'svg', 'gif'],
          })
        }
      >
        {' '}
        + Bild hinzufügen
      </LinkText>
    );
  }

  return (
    <Group
      ml={40}
      position="apart"
      p="xs"
      sx={(theme) => ({
        borderRadius: theme.radius.sm,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.lightBlue[0],
        maxWidth: 300,
      })}
    >
      <div style={{ maxWidth: 245 }}>
        <PdfPreview
          previewIcon={<IconPhoto size={20} />}
          eventId={eventId}
          file={{ entityId, entityType, fileName }}
          fileExists={fileExists}
          lastModified={undefined}
        />
      </div>
      <ActionIcon size="xs">
        <IconX onClick={() => onPictureSelect(null)} />
      </ActionIcon>
    </Group>
  );
};

export default AnswerOptionItem;
